import { getGameDateKey, formatGameDateRange } from '../../utils/dateUtils'

// GameType enum values matching backend: 0=Regular, 1=Playoff, 2=Friendly, 3=SemiFinal, 4=Final, 5=Bye
const STAGE_BY_GAME_TYPE = {
  0: 'regular',
  5: 'regular',
  1: 'playoff',
  3: 'semi',
  4: 'final',
}

const NUMBERED_STAGE_TITLES = {
  regular: 'Game Week',
  playoff: 'Playoffs Week',
}

const DAY_MS = 24 * 60 * 60 * 1000

export const getScheduleRowKey = (row) =>
  row.id || row.gameId || `game-${row.homeTeamId}-${row.awayTeamId}-${row.start}`

const getStage = (row) => {
  if (row._isSocialEvent) return 'other'
  return STAGE_BY_GAME_TYPE[row.gameType ?? 0] ?? 'other'
}

const toDayNumber = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number)
  return Math.round(Date.UTC(year, month - 1, day) / DAY_MS)
}

// Week 1 is the 7 days starting on the stage's first date; weeks without games are skipped.
const buildNumberedWeeks = (entries) => {
  if (entries.length === 0) return null
  const anchor = Math.min(...entries.map(e => e.day))
  const windows = [...new Set(entries.map(e => Math.floor((e.day - anchor) / 7)))].sort((a, b) => a - b)
  return {
    windowOf: (day) => Math.floor((day - anchor) / 7),
    numbers: new Map(windows.map((w, index) => [w, index + 1])),
  }
}

/**
 * Assigns every schedule row (games, placeholders, social events) to a section header.
 * Pass the full, unfiltered schedule so numbers and date ranges don't change with filters.
 * @param {Array} rows - Schedule rows with start, gameType, _isPlaceholder, _isSocialEvent
 * @returns {Map<string, { key: string, title: string, dateRange: string, label: string }>}
 *   Sections keyed by getScheduleRowKey(row)
 */
export const buildScheduleSections = (rows = []) => {
  const entries = rows
    .map(row => {
      const dateKey = getGameDateKey(row.start)
      return dateKey
        ? { rowKey: getScheduleRowKey(row), dateKey, day: toDayNumber(dateKey), stage: getStage(row) }
        : null
    })
    .filter(Boolean)

  const numberedWeeks = {
    regular: buildNumberedWeeks(entries.filter(e => e.stage === 'regular')),
    playoff: buildNumberedWeeks(entries.filter(e => e.stage === 'playoff')),
  }

  const findNumberedWeek = (stage, day) => {
    const weeks = numberedWeeks[stage]
    if (!weeks) return null
    const number = weeks.numbers.get(weeks.windowOf(day))
    return number ? { key: `${stage}-${number}`, title: `${NUMBERED_STAGE_TITLES[stage]} ${number}` } : null
  }

  const stageDays = (stage) => new Set(entries.filter(e => e.stage === stage).map(e => e.day))
  const singleSections = [
    { stage: 'semi', days: stageDays('semi'), section: { key: 'semi-finals', title: 'Semi-Finals' } },
    { stage: 'final', days: stageDays('final'), section: { key: 'final', title: 'Final' } },
  ]

  // Friendlies and social events on the exact day of a semi-final or final join that section,
  // so one night's rows aren't split across headers.
  const findSingleSection = (day) =>
    singleSections.find(s => s.days.has(day))?.section || null

  const assignSection = (entry) => {
    switch (entry.stage) {
      case 'regular':
      case 'playoff':
        return findNumberedWeek(entry.stage, entry.day)
      case 'semi':
      case 'final':
        return singleSections.find(s => s.stage === entry.stage).section
      default:
        return findSingleSection(entry.day)
          || findNumberedWeek('regular', entry.day)
          || findNumberedWeek('playoff', entry.day)
          || { key: `date-${entry.dateKey}`, title: null }
    }
  }

  const sectionByRow = new Map()
  const sections = new Map()
  entries.forEach(entry => {
    const { key, title } = assignSection(entry)
    const section = sections.get(key) || { key, title, firstDate: entry.dateKey, lastDate: entry.dateKey }
    if (entry.dateKey < section.firstDate) section.firstDate = entry.dateKey
    if (entry.dateKey > section.lastDate) section.lastDate = entry.dateKey
    sections.set(key, section)
    sectionByRow.set(entry.rowKey, section)
  })

  const result = new Map()
  sectionByRow.forEach((section, rowKey) => {
    const dateRange = formatGameDateRange(section.firstDate, section.lastDate)
    result.set(rowKey, {
      key: section.key,
      title: section.title,
      dateRange,
      label: section.title ? `${section.title} · ${dateRange}` : dateRange,
    })
  })
  return result
}
